import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useConfig } from "../configContext.js";
import ColorSwatchPicker from "./ColorSwatchPicker.jsx";
import { shortName } from "../calendar/mergeGroupEvents.js";

/**
 * Permanent left sidebar listing the calendars and the plan groups, with
 * their visibility and colour in reach (BeePost's calendar sidebar shape).
 *
 * Visibility and colour used to live behind the "Gérer les comptes" modal:
 * toggling one calendar meant opening a dialog, acting, closing it, and
 * looking back at the grid. Here the list sits next to the grid it drives.
 */
const COLLAPSED_KEY = "beeplanif.sidebarCollapsed";

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export default function CalendarSidebar({
  accounts,
  setAccounts,
  setNotHideAccounts,
  groups,
  setGroups,
  isGroupView,
  onAddAccount,
  onManageGroups,
  hiddenMembers,
  onToggleMember,
  onSetGroupHidden,
  setMessage,
  setColor,
  setSnackbarOpen,
}) {
  const { apiUrl } = useConfig();
  // Collapsed state sticks, like the row-density choice: someone who works
  // with the grid wide does not want to fold the panel again every morning.
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // Not persisting is survivable; the session still honours the choice.
    }
  }, [collapsed]);

  const fail = (text) => {
    setMessage(text);
    setColor("danger");
    setSnackbarOpen(true);
  };

  const toggleVisibility = async (uuid, hide) => {
    try {
      const res = await fetch(`${apiUrl}/calendars/hide/${uuid}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: uuid, hide }),
      });
      const data = await res.json();
      if (!data.success) return fail(data.message);
      setAccounts(Object.values(data.accounts || {}));
      setNotHideAccounts(Object.values(data.not_hide_accounts || {}));
    } catch (error) {
      fail("Erreur : " + error.message);
    }
  };

  const changeAccountColor = async (uuid, rgb) => {
    try {
      const res = await fetch(`${apiUrl}/calendars/${uuid}/color`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ color: `${rgb.r},${rgb.g},${rgb.b}` }),
      });
      const data = await res.json();
      if (!data.success) return fail(data.message);
      setAccounts(Object.values(data.accounts || {}));
      setNotHideAccounts(Object.values(data.not_hide_accounts || {}));
    } catch (error) {
      fail("Erreur : " + error.message);
    }
  };

  const changeGroupColor = async (groupId, rgb) => {
    try {
      const res = await fetch(`${apiUrl}/groups/${groupId}/color`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ color: `${rgb.r},${rgb.g},${rgb.b}` }),
      });
      const data = await res.json();
      if (!data.success) return fail(data.message);
      setGroups((prev) =>
        prev.map((g) => (g.id === groupId ? { ...g, color: `${rgb.r},${rgb.g},${rgb.b}` } : g)),
      );
    } catch (error) {
      fail("Erreur : " + error.message);
    }
  };

  const removeAccount = async (uuid, name) => {
    if (!window.confirm(`Retirer le calendrier de ${name} ?`)) return;
    try {
      const res = await fetch(`${apiUrl}/calendars/${uuid}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: uuid }),
      });
      const data = await res.json();
      if (!data.success) return fail(data.message);
      setAccounts(Object.values(data.accounts || {}));
      setNotHideAccounts(Object.values(data.not_hide_accounts || {}));
    } catch (error) {
      fail("Erreur : " + error.message);
    }
  };

  if (collapsed) {
    return (
      <aside className="hidden w-10 shrink-0 flex-col items-center border-r border-gray-200 bg-white py-2 md:flex">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          title="Afficher les calendriers"
          aria-label="Afficher les calendriers"
          aria-expanded={false}
          className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      </aside>
    );
  }

  return (
    <aside className="hidden w-64 shrink-0 flex-col overflow-y-auto border-r border-gray-200 bg-white md:flex">
      <div className="flex justify-end px-1 pt-2">
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          title="Replier les calendriers"
          aria-label="Replier les calendriers"
          aria-expanded
          className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
          </svg>
        </button>
      </div>
      {/* Only the list the current view is driven by. Greying the other one
          kept it taking room for something you cannot act on. */}
      {!isGroupView && (
      <Section
        title="Calendriers"
        action={{ label: "Ajouter un compte", onClick: onAddAccount }}
        emptyLabel="Aucun compte suivi."
        isEmpty={accounts.length === 0}
      >
        {accounts.map((account) => (
          <Row
            key={account.uuid}
            label={account.name}
            checked={!account.hide}
            onToggle={(checked) => toggleVisibility(account.uuid, !checked)}
            color={account.color}
            onColorChange={(rgb) => changeAccountColor(account.uuid, rgb)}
            onRemove={() => removeAccount(account.uuid, account.name)}
          />
        ))}
      </Section>
      )}

      {isGroupView && (
      <Section
        title="Groupes"
        action={{ label: "Gérer les groupes", onClick: onManageGroups }}
        emptyLabel="Aucun groupe."
        isEmpty={groups.length === 0}
      >
        {groups.map((group) => (
          <GroupRow
            key={group.id}
            group={group}
            hiddenMembers={hiddenMembers}
            onToggleMember={onToggleMember}
            onSetGroupHidden={onSetGroupHidden}
            onColorChange={(rgb) => changeGroupColor(group.id, rgb)}
          />
        ))}
      </Section>
      )}
    </aside>
  );
}

CalendarSidebar.propTypes = {
  hiddenMembers: PropTypes.instanceOf(Set),
  onToggleMember: PropTypes.func,
  onSetGroupHidden: PropTypes.func,
  accounts: PropTypes.array.isRequired,
  setAccounts: PropTypes.func.isRequired,
  setNotHideAccounts: PropTypes.func.isRequired,
  groups: PropTypes.array.isRequired,
  setGroups: PropTypes.func.isRequired,
  isGroupView: PropTypes.bool,
  onAddAccount: PropTypes.func.isRequired,
  onManageGroups: PropTypes.func.isRequired,
  setMessage: PropTypes.func.isRequired,
  setColor: PropTypes.func.isRequired,
  setSnackbarOpen: PropTypes.func.isRequired,
};

Section.propTypes = {
  title: PropTypes.string.isRequired,
  action: PropTypes.shape({ label: PropTypes.string, onClick: PropTypes.func }).isRequired,
  children: PropTypes.node,
  isEmpty: PropTypes.bool,
  emptyLabel: PropTypes.string,
};

Row.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  checked: PropTypes.bool,
  onToggle: PropTypes.func,
  // Any form toRgb understands: the backend sends [r,g,b] for accounts,
  // "r,g,b" for groups, and null before a colour is assigned.
  color: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object]),
  onColorChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
};

function Section({ title, action, children, isEmpty, emptyLabel }) {
  return (
    <section className="flex min-h-0 flex-col">
      <header className="flex items-center justify-between px-3 pt-3 pb-1">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h2>
        <button
          type="button"
          onClick={action.onClick}
          title={action.label}
          aria-label={action.label}
          className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </header>
      <div className="min-h-0 overflow-y-auto px-1 pb-2">
        {isEmpty ? (
          <p className="px-2 py-1.5 text-xs text-gray-400">{emptyLabel}</p>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

/**
 * One calendar or group line. The colour rides its own swatch rather than the
 * checkbox: a coloured checkbox conflates "is it shown" with "which colour",
 * and the two are set independently.
 */
function Row({ label, hint, checked, onToggle, color, onColorChange, onRemove }) {

  return (
    <div className="group relative flex items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50">
      {onToggle && (
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={`Afficher le calendrier de ${label}`}
          className="h-3.5 w-3.5 shrink-0 accent-[var(--brand-primary)]"
        />
      )}
      <ColorSwatchPicker
        value={color}
        onChange={onColorChange}
        label={`Changer la couleur de ${label}`}
        className="h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-black/10"
      />
      <span className="min-w-0 flex-1 truncate text-sm text-gray-700" title={label}>
        {label}
        {hint && <span className="ml-1 text-xs text-gray-400">{hint}</span>}
      </span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          title="Retirer"
          aria-label={`Retirer ${label}`}
          className="hidden h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-600 group-hover:flex"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * A group, foldable to reveal its members with a checkbox each.
 *
 * On a group of a hundred people the useful question is rarely "all of them?"
 * but "these four" — hence a per-member toggle rather than only a group one.
 * The group's own checkbox shows and acts on the whole set, and reads as
 * indeterminate when only some members are shown.
 */
function GroupRow({ group, hiddenMembers, onToggleMember, onSetGroupHidden, onColorChange }) {
  const [open, setOpen] = useState(false);
  const groupCheckbox = useRef(null);

  const members = group.members || [];
  const uuids = members.map((m) => m.account_uuid);
  const shownCount = uuids.filter((uuid) => !hiddenMembers?.has(uuid)).length;
  const allShown = shownCount === uuids.length;

  useEffect(() => {
    if (groupCheckbox.current) {
      groupCheckbox.current.indeterminate = shownCount > 0 && !allShown;
    }
  }, [shownCount, allShown]);

  return (
    <div className="relative">
      <div className="group flex items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50">
        <input
          ref={groupCheckbox}
          type="checkbox"
          checked={allShown && uuids.length > 0}
          onChange={(e) => onSetGroupHidden?.(uuids, !e.target.checked)}
          aria-label={`Afficher tous les membres de ${group.name}`}
          className="h-3.5 w-3.5 shrink-0 accent-[var(--brand-primary)]"
        />
        <ColorSwatchPicker
          value={group.color}
          onChange={onColorChange}
          label={`Changer la couleur de ${group.name}`}
          className="h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-black/10"
        />
        <button
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-1 text-left"
        >
          <svg
            className={`h-3 w-3 shrink-0 text-gray-400 transition-transform ${open ? "rotate-90" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="truncate text-sm text-gray-700" title={group.name}>
            {group.name}
          </span>
          <span className="shrink-0 text-xs text-gray-400">
            {allShown ? uuids.length : `${shownCount}/${uuids.length}`}
          </span>
        </button>
      </div>

      {/* The list is bounded: forty members unfolded would push every other
          group out of view and turn the sidebar into one long scroll. */}
      {open && (
        <ul className="mb-1 ml-6 max-h-64 overflow-y-auto border-l border-gray-200 pl-2">
          {members.map((member) => (
            <li key={member.account_uuid}>
              <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={!hiddenMembers?.has(member.account_uuid)}
                  onChange={() => onToggleMember?.(member.account_uuid)}
                  className="h-3 w-3 shrink-0 accent-[var(--brand-primary)]"
                />
                <span className="truncate text-xs text-gray-600" title={member.account_name}>
                  {shortName(member.account_name)}
                </span>
              </label>
            </li>
          ))}
          {members.length === 0 && (
            <li className="px-1 py-1 text-xs text-gray-400">Aucun membre.</li>
          )}
        </ul>
      )}

    </div>
  );
}

GroupRow.propTypes = {
  group: PropTypes.object.isRequired,
  hiddenMembers: PropTypes.instanceOf(Set),
  onToggleMember: PropTypes.func,
  onSetGroupHidden: PropTypes.func,
  onColorChange: PropTypes.func.isRequired,
};
